import { stateLabelsDictionary, stateOptions } from "../app/constants/au-states.constant";

export class Address {
  normalizedFullAddress: string;
  careOf: string;
  street: string;
  line2: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  gnafID: string;
  meshBlock: string;
  level: string;
  buildingName: string;

  constructor(data: Partial<Address> = {}) {
    this.normalizedFullAddress = Address.patchNormalizedFullAddress(data.normalizedFullAddress);
    this.careOf = data.careOf ?? '';
    this.street = data.street ?? '';
    this.line2 = data.line2 ?? '';
    this.suburb = data.suburb ?? '';
    this.state = data.state ?? '';
    this.postcode = data.postcode ?? '';
    this.country = data.country ?? '';
    this.gnafID = data.gnafID ?? '';
    this.meshBlock = data.meshBlock ?? '';
    this.level = data.level ?? '';
    this.buildingName = data.buildingName ?? '';
  }

  isEqual(address: Address): boolean {
    if (this.isEmpty() || address.isEmpty()) return false;

    const exceptProps: (keyof Address)[] = ['normalizedFullAddress'];

    return Object.entries(this).every(([key, value]) => {
      if (exceptProps.includes(key as keyof Address)) return true;
      return value === address[key];
    });
  }

  isEmpty(exceptKeys: (keyof Address)[] = []): boolean {
    return Object.entries(this)
      .filter(([key, _]) => !(exceptKeys as string[]).includes(key))
      .every(([_, value]) => !value);
  }

  static patchNormalizedFullAddress(normalizedFullAddress?: string): string {
    if (!normalizedFullAddress || (normalizedFullAddress && normalizedFullAddress.trim() === ','))
      return '';
    return normalizedFullAddress;
  }

  static buildNormalizedFullAddress(address: Address): string {
    // BE string $"{(string.IsNullOrEmpty(Line2) ? "" : $"{Line2} ")}{Street}, {Suburb} {State} {Postcode}";
    return `${ address.line2 ?? '' } ${ address.street ?? '' }, ${ address.suburb ?? '' } ${ address.state ?? '' } ${ address.postcode ?? '' }`
      .trim() // remove spaces by sides
      .replace(/\s+/g, ' '); // replace multiple spaces in string with one space
  }

  static parseNormalizedFullAddress(normalizedFullAddress: string): Address {
    if (!normalizedFullAddress.trim().replaceAll(',', '')) return new Address();

    const findPostcode = (fullAddress: string): { postcode: string, rest: string } => {
      const postcodeRegex = /\d{4}$/;
      const match = fullAddress.match(postcodeRegex);

      if (match?.[0]) {
        const indexStart = fullAddress.indexOf(match[0]);
        return { postcode: match[0] , rest: fullAddress.substring(0, indexStart).trim() };
      }

      return { postcode: '', rest: fullAddress };
    }

    const findAustralianState = (fullAddress: string): { state: string, rest: string } => {
      const lowerCaseFullAddress = fullAddress.toLowerCase();
      const allStates = Object.entries(stateLabelsDictionary).flat();
      const stateRegex = new RegExp(`\\b(${allStates.join('|').toLowerCase()})\\b`, 'gi');
      const match = lowerCaseFullAddress.match(stateRegex);

      if (match?.[0]) {
        const selectedStateKey = stateOptions.find((option) => (option.value as string).toLowerCase() === match[0] || option.label.toLowerCase() === match[0])?.value as string ?? '';
        const indexStart = lowerCaseFullAddress.indexOf(match[0]);
        return { state: selectedStateKey?.toUpperCase() , rest: (fullAddress.slice(0, indexStart) + fullAddress.slice(indexStart + match[0].length)).trim() };
      }

      return { state: '', rest: fullAddress };
    }

    const findSuburb = (fullAddress: string): { suburb: string, rest: string } => {
      const parts = fullAddress
        .split(',')
        .filter((addressPart) => {
          const part = addressPart.trim();
          return part && part !== '.'
        });

      const streetAddress = parts[0] ?? '';
      const suburb = parts[1] ?? ''

      return { suburb, rest: streetAddress };
    }

    const postcodeObj = findPostcode(normalizedFullAddress);
    const postcode = postcodeObj.postcode;
    const postcodeRest = postcodeObj.rest;

    const stateObj = findAustralianState(postcodeRest);
    const state = stateObj.state
    const stateRest = stateObj.rest;

    const suburbObj = findSuburb(stateRest);
    const suburb = suburbObj.suburb;
    const street = suburbObj.rest;

    const address = new Address({
      state,
      postcode,
      suburb,
      street,
      normalizedFullAddress
    });

    Object.keys(address)
      .forEach((key) => address[key] = address[key].trim());

    return address;
  }
}
